import type { Capacidad } from "./api";

/**
 * Normaliza para buscar: sin acentos, sin mayúsculas, sin espacios de sobra.
 *
 * Nadie teclea «Guadalupe» con la tilde correcta cuando tiene prisa, y una
 * búsqueda que exige el acento es una búsqueda que no encuentra. Mismo
 * criterio que usa el resolvedor de domicilios del tronco.
 */
export function sinAcentos(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Cómo se lee cada capacidad en una lista apretada: una o dos palabras que
 *  dicen el oficio, no la clave del sistema.
 *
 *  Está AQUÍ y no en la base a propósito: `sistema.capacidad.descripcion` es
 *  una frase completa para explicar al asignar permisos («Dar de alta,
 *  editar y desactivar cuentas…»), y una tabla necesita la versión corta. Son
 *  dos textos con dos trabajos, no una duplicación.
 *
 *  Una capacidad que no esté aquí se muestra por su clave: se ve fea y se
 *  nota, que es justo lo que debe pasar para que alguien la agregue. */
const CORTO: Record<string, string> = {
  administrar_usuarios: "Cuentas",
  ver_eventos: "Bitácora",
  territorio_completo: "Todo el territorio",
  capturar: "Captura",
  ver_padron: "Padrón",
  conciliar: "Bandeja",
  administrar_metas: "Metas",
  ver_metas: "Ver metas",
  ver_prioridad: "Prioridad",
  ver_rutas: "Rutas",
  gestionar_captacion: "Captación",
  ver_consentimiento: "Consentimiento",
  atender_arco: "ARCO",
  operar: "Llamadas",
  supervisar_operacion: "Supervisar cola",
  exportar: "Exportar",
  administrar_contacto: "Campañas",
  ver_contacto: "Ver campañas",
  administrar_conectores: "Conectores",
  ver_conectores: "Ver conectores",
  correr_conector: "Importar",
  revertir_conector: "Revertir",
  verificar_identidad: "Verificar",
  revisar_identidad: "Revisar identidad",
  administrar_alianzas: "Aliados",
  autorizar_alianza: "Autorizar aliado",
  gestionar_confianza: "Confianza",
  ver_confianza: "Ver confianza",
  administrar_targeting: "Targeting",
  ver_targeting: "Ver targeting",
};

export interface ResumenDeCapacidades {
  /** Las primeras, listas para dibujar. */
  etiquetas: string[];
  /** Cuántas quedaron fuera. Cero si caben todas. */
  mas: number;
}

/**
 * De un jsonb de capacidades a lo que cabe en una celda.
 *
 * Antes la columna decía un NÚMERO («7»), que no dice nada: dos cuentas con
 * siete capacidades pueden ser un administrador y un capturista con permisos
 * de más, y en una tabla de permisos esa diferencia es justo la que hay que
 * ver sin abrir nada.
 *
 * El orden no es alfabético: primero lo que más manda, porque si solo se ven
 * tres hay que ver las tres que importan.
 */
const ORDEN = [
  "territorio_completo",
  "administrar_usuarios",
  "administrar_conectores",
  "administrar_contacto",
  "administrar_targeting",
  "administrar_metas",
  "gestionar_confianza",
  "atender_arco",
  "conciliar",
  "operar",
  "capturar",
  "exportar",
];

export function resumirCapacidades(
  capacidades: Record<string, boolean>,
  catalogo?: Map<string, Capacidad>,
  cuantas = 3,
): ResumenDeCapacidades {
  const activas = Object.entries(capacidades)
    .filter(([, v]) => v === true)
    .map(([k]) => k);

  activas.sort((a, b) => {
    const ia = ORDEN.indexOf(a);
    const ib = ORDEN.indexOf(b);
    if (ia !== ib) return (ia < 0 ? ORDEN.length : ia) - (ib < 0 ? ORDEN.length : ib);
    return a.localeCompare(b, "es");
  });

  const nombre = (id: string) => CORTO[id] ?? catalogo?.get(id)?.id ?? id;
  return {
    etiquetas: activas.slice(0, cuantas).map(nombre),
    mas: Math.max(0, activas.length - cuantas),
  };
}
